import React, { useState, useMemo, useCallback } from 'react';
import { Package, Plus, X } from '@phosphor-icons/react';
import chestStyles from './Sections/Items/ItemChest.module.css';
import { formatCurrencyShort as formatCurrency } from '../../utils/formatters';

/**
 * MiniItemPicker — inline item picker for VendorCard.
 * Extracts the core selection logic from ItemChest without the sidebar chrome.
 */
function MiniItemPicker({
  masterItems = [],
  suggestedItems = [],
  projectItems = [],
  onAddItems,
  isLoading = false,
  onClose,
}) {
  const [selectedItems, setSelectedItems] = useState(new Set());

  // IDs of items already in the space
  const addedItemIds = useMemo(() => {
    const ids = new Set();
    (projectItems || []).forEach(pi => {
      if (pi.master_item_id) ids.add(pi.master_item_id);
      if (pi.relationships?.bundle?.master_item_id) {
        ids.add(pi.relationships.bundle.master_item_id);
      }
    });
    return ids;
  }, [projectItems]);

  // Split into suggested and other, sorted by reference_suggested_items.display_order
  // (when the item has a row for this space) then master_items.display_order, then name.
  const { suggested, other } = useMemo(() => {
    if (!masterItems) return { suggested: [], other: [] };

    const spaceOrderMap = new Map();
    (suggestedItems || []).forEach(si => {
      spaceOrderMap.set(si.master_item_id, si.display_order ?? 9999);
    });
    const suggestedIds = new Set(
      (suggestedItems || []).filter(si => si.is_suggested).map(si => si.master_item_id),
    );

    const s = [];
    const o = [];
    masterItems.forEach(mi => {
      if (suggestedIds.has(mi.id)) s.push(mi);
      else o.push(mi);
    });

    const sorter = (a, b) => {
      const ap = spaceOrderMap.get(a.id) ?? 9999;
      const bp = spaceOrderMap.get(b.id) ?? 9999;
      if (ap !== bp) return ap - bp;
      const as = a.display_order ?? 9999;
      const bs = b.display_order ?? 9999;
      if (as !== bs) return as - bs;
      return (a.item_name || '').localeCompare(b.item_name || '');
    };
    s.sort(sorter);
    o.sort(sorter);
    return { suggested: s, other: o };
  }, [masterItems, suggestedItems]);

  const toggleItem = useCallback(itemId => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    if (selectedItems.size === 0) return;
    const items = Array.from(selectedItems)
      .map(id => masterItems.find(mi => mi.id === id))
      .filter(Boolean);
    onAddItems(items);
    setSelectedItems(new Set());
  }, [selectedItems, masterItems, onAddItems]);

  const renderItem = (item, isAdded) => {
    const isSelected = selectedItems.has(item.id);
    return (
      <div
        key={item.id}
        className={`${chestStyles.itemRow} ${isSelected ? chestStyles.selected : ''}`}
        onClick={() => toggleItem(item.id)}
      >
        <input
          type="checkbox"
          className={chestStyles.itemCheckbox}
          checked={isSelected}
          onChange={e => {
            e.stopPropagation();
            toggleItem(item.id);
          }}
          onClick={e => e.stopPropagation()}
        />
        <div className={chestStyles.itemInfo}>
          <div className={chestStyles.itemName}>{item.item_name}</div>
          <div className={chestStyles.itemMeta}>
            {item.estimated_cost > 0 && (
              <span className={chestStyles.itemCost}>{formatCurrency(item.estimated_cost)}</span>
            )}
            {item.is_bundle && (
              <span className={chestStyles.bundleIcon} title="חבילה">
                <Package size={14} />
              </span>
            )}
          </div>
        </div>
        {isAdded && <span className={chestStyles.alreadyAdded}>נוסף</span>}
      </div>
    );
  };

  const allItems = [...suggested, ...other];

  return (
    <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 16px' }}>
      {/* Header with close */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>בחר פריטים להוספה</span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#94a3b8',
            padding: 2,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Item list */}
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {suggested.length > 0 && (
          <div
            style={{
              marginBottom: 8,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '6px 0',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#92400e',
                padding: '0 12px 4px',
              }}
            >
              פריטים מומלצים
            </div>
            {suggested.map(item => renderItem(item, addedItemIds.has(item.id)))}
          </div>
        )}
        {other.length > 0 && (
          <div>
            {suggested.length > 0 && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#64748b',
                  padding: '4px 12px',
                }}
              >
                פריטים נוספים
              </div>
            )}
            {other.map(item => renderItem(item, addedItemIds.has(item.id)))}
          </div>
        )}
        {allItems.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 16, fontSize: 13 }}>
            אין פריטים זמינים
          </div>
        )}
      </div>

      {/* Add button */}
      <button
        className={chestStyles.addButton}
        onClick={handleAdd}
        disabled={selectedItems.size === 0 || isLoading}
        style={{ marginTop: 8, width: '100%' }}
      >
        <Plus size={16} />
        הוסף {selectedItems.size > 0 ? `(${selectedItems.size})` : ''} פריטים
      </button>
    </div>
  );
}

export default React.memo(MiniItemPicker);
